import React, { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Heading,
  HStack,
  Stack,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import CustomTab from '../Tabs/CustomTab';
import QuestionCardCertification from '../Card/Question-card-certification';
import AvisoInline from '../Feedback/AvisoInline';
import IdentidadeDoPilar from './IdentidadeDoPilar';

/**
 * EG-42 / AD-2 + AD-4 — a estrutura do questionário, derivada dos templates.
 *
 * A tela deixa de ser onde o gestor MONTA a estrutura e passa a ser onde ele a
 * VÊ: pilares e temas vêm da classificação que as questões do template já
 * carregam.
 *
 * SOBRE A APARÊNCIA (pedido do dev no teste exploratório)
 *   A primeira versão desenhou cards e abas próprios, e ficou visivelmente mais
 *   pobre que as telas de edição. Agora usa exatamente as mesmas peças:
 *     CustomTab                  — a aba do painel, com a borda arredondada
 *     Heading 20px + borderBottom — o cabeçalho de pilar de PillarTabEdit
 *     barra #E6F1F8 + texto #0073BB — o cabeçalho de tema de PillarTabEdit
 *     QuestionCardCertification   — o MESMO card da edição, não uma cópia
 *
 *   Reusar o card em vez de reimplementá-lo importa além do visual: ele já
 *   resolve pergunta obrigatória, opções, evidência e o rótulo de classificação.
 *   Uma segunda implementação divergiria no primeiro ajuste.
 *
 * As mensagens não são enfeite. O estado "nada classificado" é o estado NORMAL
 * hoje — 6 de 9.754 question_template classificados —, então todo questionário
 * novo nasce assim até a passada em massa da EG-41 rodar em produção. Uma tela
 * que só mostrasse vazio pareceria quebrada, e o gestor abriria um bug em vez de
 * pedir a classificação dos templates.
 *
 * @param exigeTema      true no periódico (pilar > tema > questão), false no
 *                       certificado, cuja estrutura é só pilar.
 * @param onRemoverQuestao opcional. Presente, cada card ganha o botão de remover
 *                       da edição — remover aqui é só não criar a questão, e não
 *                       tem relação com a AD-9, que trata de classificação.
 */

const CabecalhoDePilar = ({ children }) => (
  <Heading
    fontSize='20px'
    fontWeight={600}
    lineHeight='23.48px'
    p='6'
    borderBottom='2px solid #F5F5FB'
  >
    {children}
  </Heading>
);

const BarraDeTema = ({ children }) => (
  <HStack h='51px' bg='#E6F1F8' p='16px'>
    <Text color='#0073BB' fontWeight={600}>
      {children}
    </Text>
  </HStack>
);

const ListaDeQuestoes = ({ questoes, onRemoverQuestao }) => (
  <Stack spacing='16px' p='6'>
    {questoes.map((questao) => (
      <QuestionCardCertification
        key={questao.id_question_template}
        question={questao}
        {...(onRemoverQuestao && {
          remove: () => onRemoverQuestao(questao.id_question_template),
        })}
      />
    ))}
  </Stack>
);

export default function EstruturaDerivada({
  estrutura,
  exigeTema = true,
  onRemoverQuestao,
}) {
  const pilares = estrutura?.pilares ?? [];
  const semClassificacao = estrutura?.semClassificacao ?? [];

  // AS ABAS SÃO CONTROLADAS, E POR IDENTIDADE — NÃO POR POSIÇÃO.
  //
  // O <Tabs> era não controlado, então o Chakra guardava o índice por dentro. Ao
  // remover um template, os pilares que só ele trazia desapareciam e o índice
  // guardado passava a apontar para outro lugar: se o pilar removido era o
  // selecionado, a tela ficava com sensação de vazio; se o removido vinha ANTES
  // do selecionado, as abas escorregavam e aparecia o conteúdo do pilar errado
  // sem que nada acusasse.
  //
  // Guardar a CHAVE do que está aberto, e não o índice, resolve os dois casos de
  // uma vez: pilar removido cai na primeira aba (foi o pedido), e pilar removido
  // antes do atual mantém o mesmo pilar aberto, que é o que o gestor espera.
  //
  // É a segunda vez que a contagem de abas morde nesta story — a primeira foi
  // aba sem painel correspondente. Aba é lista derivada de dado que muda; tratar
  // como índice fixo é o erro.
  const chavesDasAbas = [
    ...pilares.map((pilar) => `pilar-${pilar.id_pillar}`),
    ...(semClassificacao.length > 0 ? ['sem-classificacao'] : []),
  ];
  const assinaturaDasAbas = chavesDasAbas.join('|');

  const [abaSelecionada, setAbaSelecionada] = useState(chavesDasAbas[0]);

  // Normaliza o estado quando o conjunto de abas muda. A forma de atualização por
  // função evita depender de abaSelecionada e deixa a dependência única e estável.
  useEffect(() => {
    setAbaSelecionada((atual) => {
      const chaves = assinaturaDasAbas ? assinaturaDasAbas.split('|') : [];
      if (chaves.length === 0) return atual;
      return chaves.includes(atual) ? atual : chaves[0];
    });
  }, [assinaturaDasAbas]);

  // O índice do render não espera o efeito: se a chave já não existe, cai em 0
  // agora. Sem isso haveria um frame com o painel errado — curto, mas visível.
  const indiceAtivo = Math.max(0, chavesDasAbas.indexOf(abaSelecionada));

  if (pilares.length === 0 && semClassificacao.length === 0) {
    return (
      <Box px='6' pb='6'>
        <AvisoInline tom='info'>
          Selecione um ou mais templates para montar a estrutura do questionário.
          Os pilares e temas vêm da classificação das perguntas.
        </AvisoInline>
      </Box>
    );
  }

  return (
    <Stack spacing='6'>
      {pilares.length === 0 && (
        <AvisoInline tom='warning'>
          Nenhuma das perguntas selecionadas está classificada, então não há
          pilares a exibir. Classifique as perguntas no template e propague para
          que a estrutura apareça aqui.
        </AvisoInline>
      )}

      {/* O cartão branco é do CHAMADOR, não deste componente: a certificação
          renderiza direto no Container e precisa de cartão; o periódico já vive
          dentro de um, e um cartão aqui viraria branco sobre branco. */}
      <Tabs
        isLazy
        index={indiceAtivo}
        onChange={(indice) => setAbaSelecionada(chavesDasAbas[indice])}
      >
        <TabList gap='12px' px='6' border='none' userSelect='none'>
          {pilares.map((pilar) => (
            <CustomTab key={pilar.id_pillar}>{pilar.name_pillar}</CustomTab>
          ))}
          {semClassificacao.length > 0 && (
            <CustomTab>
              Sem classificação
              <Badge ml='2' colorScheme='orange' borderRadius='full'>
                {semClassificacao.length}
              </Badge>
            </CustomTab>
          )}
        </TabList>

        <TabPanels>
          {pilares.map((pilar) => (
            <TabPanel key={pilar.id_pillar} p='0'>
              <CabecalhoDePilar>{pilar.name_pillar}</CabecalhoDePilar>

              {/* Decisão do dev: a identidade do pilar (imagem e texto) aparece
                  também na criação, em somente leitura — quem monta precisa ver
                  com que cara o pilar vai chegar a quem responde. Editar continua
                  sendo só no catálogo. */}
              <IdentidadeDoPilar
                imgPillar={pilar.img_pillar}
                textPillar={pilar.text_pillar}
              />

              {exigeTema ? (
                pilar.temas.map((tema) => (
                  <Box key={tema.id_theme}>
                    <BarraDeTema>Perguntas - {tema.title}</BarraDeTema>
                    <ListaDeQuestoes
                      questoes={tema.questoes}
                      onRemoverQuestao={onRemoverQuestao}
                    />
                  </Box>
                ))
              ) : (
                <>
                  {/* No certificado não há sanfona de tema, mas a barra azul de
                      "Perguntas" existe na tela de edição e é o que separa o
                      cabeçalho do pilar da lista. Sem ela a criação ficava
                      visivelmente mais pobre que a edição. */}
                  <BarraDeTema>Perguntas</BarraDeTema>
                  <ListaDeQuestoes
                    questoes={pilar.questoes}
                    onRemoverQuestao={onRemoverQuestao}
                  />
                </>
              )}
            </TabPanel>
          ))}

          {semClassificacao.length > 0 && (
            <TabPanel p='0'>
              <CabecalhoDePilar>Sem classificação</CabecalhoDePilar>
              <Box px='6' pt='6'>
                <AvisoInline tom='warning'>
                  Estas perguntas entram no questionário sem pilar e sem tema.
                  Para classificá-las, classifique a pergunta no template e
                  propague — a classificação do questionário vem do template.
                </AvisoInline>
              </Box>
              <Box mt='6'>
                <BarraDeTema>Perguntas</BarraDeTema>
              </Box>
              <ListaDeQuestoes
                questoes={semClassificacao}
                onRemoverQuestao={onRemoverQuestao}
              />
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>
    </Stack>
  );
}
